<template>
    <draggable
        id="bioPanelRef"
        :initialX="initPosX"
        :initialY="50"
        :minY="10"
        class="bio-panel__container"
    >
        <div class="bio-panel__content" @mousedown.stop>
            <div class="bio-panel__head-title">
                {{ $t('patent-view.lifescience.sequenceTitle') }}
            </div>
            <div class="bio-panel__close-btn" @click="onClose">
                <svg class="snap-zen-icon icon" aria-hidden="true">
                    <use xlink:href="#zen-icon-cross-middle" />
                </svg>
            </div>
            <div v-if="bioPanelLoading" class="bio-panel__loading-container">
                <v-loading :visible="true" class="loading-icon" />
            </div>
            <div v-else class="bio-panel__content-container">
                <PtTabs
                    v-model="selectTab"
                    class="bio__structures-tab"
                    :disabled="isLoading"
                    @tab-click="onClickBioPanelTab"
                >
                    <PtTabPane
                        v-if="hasBioSequences"
                        :label="$t('patent-view.lifescience.highlight')"
                        name="highlight"
                    />
                    <PtTabPane
                        v-if="hasBioSequences"
                        :label="$t('patent-view.lifescience.extract.tab')"
                        name="extract"
                    />
                    <PtTabPane :label="$t('patent-view.lifescience.family.tab')" name="family" />
                </PtTabs>
                <div v-show="selectTab === 'highlight'" class="bio__structures__highlight">
                    <div class="bio-panel__title">
                        <span class="bio-panel__title-text">
                            {{ $t('patent-view.lifescience.sequenceHighlighter') }}</span>
                        <PtToggler
                            class="switch-button"
                            :value="bioHighlightEnable"
                            @input="onSwitchEnable(!bioHighlightEnable)"
                        />
                    </div>
                    <ElRadioGroup
                        v-if="fromBio"
                        v-model="highlightTabName"
                        class="bio__structures-radio"
                        :disabled="isLoading"
                        size="mini"
                    >
                        <ElRadioButton label="selected">
                            {{ $t('bio.tabs.selected') }}
                        </ElRadioButton>
                        <ElRadioButton label="upload" :class="{ 'only-input': !fromBio }">
                            {{ $t('bio.tabs.upload') }}
                        </ElRadioButton>
                    </ElRadioGroup>
                    <div
                        class="bio__structures-select"
                    >
                        <div v-show="highlightTabName === 'selected'" class="bio-sequence">
                            <div class="bio-sequence__label">
                                {{ $t('bio.selectSeq') }}
                            </div>
                            <div class="bio-sequence__row">
                                <BioDropdown
                                    class="bio-sequence__select"
                                    type="group"
                                    hideSwitch
                                    :dataSet="dropDownDataSet"
                                    :loadDataFn="loadSequenceDetail"
                                    :defaultSelected="target"
                                    :disabled="isLoading"
                                    isLazyLoadData
                                    @select="handleSelectTarget"
                                />
                            </div>
                        </div>
                        <div v-show="highlightTabName === 'upload'" class="bio-sequence__row">
                            <div class="upload-input-wrap">
                                <PtInput
                                    ref="ptInput"
                                    v-model="inputSequence"
                                    class="bio-textarea"
                                    type="textarea"
                                    :disabled="isLoading"
                                    :placeholder="$t('bio.assistant.placeholder')"
                                    :autosize="{ minRows: 2, maxRows: 4 }"
                                    @change="handleUpdateUploadSeq"
                                />
                                <span v-if="isMaxSearchResultCount" class="bio-sequence__search-tip">
                                    *{{ $t('patent-view.lifescience.searchTip') }}
                                </span>
                                <ElPopover
                                    :visibleArrow="false"
                                    placement="left"
                                    width="626"
                                    popperClass="bio-panel__setting-panel"
                                    @hide="() => { showSettingUpload = false; }"
                                >
                                    <AssistantPresetStateProvider
                                        :manualExpand.sync="userManualExpand"
                                        :shortExpand.sync="userShortExpand"
                                    >
                                        <BioSearchSetting
                                            storeKey="upload"
                                            :sequenceType="currentSetting.sequenceType"
                                            :dbType="currentSetting.dbType"
                                            :searchType="currentSearchType"
                                            @reset="handleReset"
                                        />
                                    </AssistantPresetStateProvider>
                                    <a
                                        slot="reference"
                                        class="bio-sequence__row--setting bio-upload--setting"
                                        :class="{ 'changed': currentSetting.isChanged, 'disabled': isLoading }"
                                        href="javascript:;"
                                        @click="showUploadSetting"
                                    >
                                        {{ $t('bio.setting') }}
                                        <PtIcon icon="SolidArrowRight" class="bio-upload--setting__arrow-right" />
                                    </a>
                                </ElPopover>
                            </div>
                            <div v-if="!uploadSequenceValid" class="bio-error-info">
                                *{{
                                    $t('bio.tip.uploadError')
                                }}
                            </div>
                        </div>

                        <button
                            v-show="highlightTabName === 'upload'"
                            :disabled="!inputSequence.length || isLoading || !uploadSequenceValid"
                            class="pt-ui-btn bio-findseq"
                            data-type="primary"
                            @click="handleSearchSequence"
                        >
                            {{ $t('bio.findSeqBtn') }}
                        </button>
                        <div v-if="isLoading" class="bio-panel__loading">
                            <PtIcon icon="SolidLoadingBars" />
                        </div>
                        <div
                            v-show="!isLoading && showSearchSequence"
                            v-infinite-scroll="loadMoreSequence"
                            class="bio-panel__structures-content"
                            infinite-scroll-disabled="busy"
                            infinite-scroll-immediate="false"
                        >
                            <div v-if="bioQuerySequence">
                                <div class="bio-panel__list-title">
                                    <span class="bio-panel__dot bio-panel__dot-exact" />
                                    <span class="bio-panel__selected">
                                        <span>{{ $t('bio.exact') }}({{ sequenceResult.exact_matches ? sequenceResult.exact_matches.length : 0 }})</span>
                                        <ElTooltip
                                            effect="light"
                                            :content="$t('bio.tip.exactTip')"
                                            placement="top"
                                            :visibleArrow="false"
                                        >
                                            <PtIcon icon="SolidInfo" />
                                        </ElTooltip>
                                    </span>
                                    <span class="bio-panel__jump-title">{{ $t('bio.panelLocation') }}</span>
                                </div>
                                <div
                                    v-if="sequenceResult.exact_matches && sequenceResult.exact_matches.length"
                                    @keydown.up="onKeyboardUpAndDown($event, 'prev')"
                                    @keydown.down="onKeyboardUpAndDown($event, 'next')"
                                >
                                    <BioHighlightItem
                                        v-for="(item, index) in sequenceResult.exact_matches"
                                        :key="index"
                                        ref="highlightItems"
                                        highlightType="exact"
                                        :seqData="item"
                                        @update-seqid="updateSeqId"
                                    />
                                </div>
                                <div class="bio-panel__list-title">
                                    <span class="bio-panel__dot bio-panel__dot-selected" />
                                    <span class="bio-panel__selected">
                                        <span>{{ $t('bio.selected') }}({{ sequenceResult.similar_match_total || 0 }})</span>
                                        <ElTooltip
                                            effect="light"
                                            :content="$t('bio.tip.similarTip')"
                                            placement="top"
                                            :visibleArrow="false"
                                        >
                                            <PtIcon icon="SolidInfo" />
                                        </ElTooltip>
                                    </span>
                                    <span class="bio-panel__jump-title">{{ $t('bio.panelLocation') }}</span>
                                </div>
                                <div
                                    v-if="sequenceResult.similar_match && sequenceResult.similar_match.length"
                                    @keydown.up="onKeyboardUpAndDown($event, 'prev')"
                                    @keydown.down="onKeyboardUpAndDown($event, 'next')"
                                >
                                    <BioHighlightItem
                                        v-for="(item, index) in sequenceResult.similar_match"
                                        :key="index"
                                        ref="highlightItems"
                                        highlightType="selected"
                                        :seqData="item"
                                        @update-seqid="updateSeqId"
                                    />
                                </div>
                                <div class="bio-panel__list-title">
                                    <span class="bio-panel__dot bio-panel__dot-others" />
                                    <span class="bio-panel__selected">
                                        {{ $t('bio.others') }}({{ sequenceResult.other_sequence_total || 0 }})
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div v-show="selectTab === 'extract'">
                    <div class="bio-panel__title">
                        <span class="bio-panel__title-text">
                            {{ $t('patent-view.lifescience.extract.select') }}
                        </span>
                    </div>

                    <div class="bio__structures__extract">
                        <ElRadioGroup v-model="extractType">
                            <div class="bio__structures__extract__row">
                                <ElRadio label="EXTRACT_ALL">
                                    {{ $t('patent-view.lifescience.extract.selectAll') }}
                                </ElRadio>
                                <ElPopover
                                    :visibleArrow="false"
                                    popperClass="bio-panel__title-tip"
                                    placement="right"
                                    trigger="hover"
                                    :content="$t('patent-view.lifescience.extract.selectAllTip')"
                                >
                                    <PtIcon slot="reference" icon="SolidInfo" />
                                </ElPopover>
                            </div>
                            <div class="bio__structures__extract__row">
                                <ElRadio label="EXTRACT_RANGE">
                                    {{ $t('patent-view.lifescience.extract.selectRange') }}
                                </ElRadio>
                            </div>
                        </ElRadioGroup>
                        <div v-if="extractType === 'EXTRACT_RANGE'" class="bio__structures__range">
                            <label>
                                <input
                                    v-model="extractList"
                                    :disabled="!maxIdNum"
                                    type="checkbox"
                                    value="EXTRACT_NUM"
                                >
                                <span>
                                    {{ $t('patent-view.lifescience.extract.list') }}
                                </span>
                                <ElPopover
                                    :visibleArrow="false"
                                    popperClass="bio-panel__title-tip"
                                    placement="top"
                                    trigger="hover"
                                    :content="$t('patent-view.lifescience.extract.tip')"
                                >
                                    <PtIcon slot="reference" icon="SolidInfo" />
                                </ElPopover>
                            </label>
                            <div class="bio__structures__range-seq">
                                {{ $t('patent-view.lifescience.extract.maxIdNum', [maxIdNum.toLocaleString()]) }}
                            </div>
                            <div class="bio__download-rows-left">
                                SEQ ID
                                <div
                                    class="bio__download-rows-left-inputs"
                                    :class="{
                                        'bio__download-rows-left-inputs-error': rangeCountNotLegal
                                    }"
                                >
                                    <input v-model="sequenceIdString" :placeholder="$t('bio.seqIdPlaceholder')">
                                </div>
                            </div>
                            <div v-show="isShowRangeError" class="bio__download-errors">
                                {{ errorMsg }}
                            </div>

                            <label :class="{ disabled: !manualCount }">
                                <input
                                    v-model="extractList"
                                    :disabled="!manualCount"
                                    type="checkbox"
                                    value="EXTRACT_MANUAL"
                                >
                                {{ $t('patent-view.lifescience.extract.other') }}({{ manualCount }})
                            </label>
                        </div>
                    </div>
                    <div class="bio__structures__extract">
                        <div class="bio__download-rows-download">
                            <div class="button-container">
                                <!-- 添加到msa -->
                                <ElTooltip
                                    :visibleArrow="false"
                                    :disabled="!save2WorkspaceTip"
                                    placement="top"
                                    :content="save2WorkspaceTip"
                                    :effect="msaButtonDisabled ? 'light' : 'dark'"
                                >
                                    <div
                                        class="icon-button"
                                        :class="{
                                            'icon-disable': msaButtonDisabled
                                        }"
                                        @click="preAddToMsa"
                                    >
                                        <PtIcon
                                            icon="SolidFolderMany"
                                            :style="{fill: msaButtonDisabled ? '#B3BAC5' : '#505F79'}"
                                        />
                                    </div>
                                </ElTooltip>

                                <!-- 导出功能 -->
                                <ElPopover
                                    :visibleArrow="false"
                                    :disabled="!isOverDownloadLimit"
                                    popperClass="bio-panel__popover"
                                    width="200"
                                    placement="top"
                                    trigger="hover"
                                    :content="$t('bio.tip.export', [MAX_LIMIT_SEQUENCE_DOWNLOAD])"
                                >
                                    <span slot="reference">
                                        <button
                                            class="pt-ui-btn"
                                            data-type="default"
                                            :loading="exportLoading"
                                            :disabled="exportButtonDisabled"
                                            @click="exportSequence"
                                        >
                                            <PtIcon
                                                :style="{fill: exportButtonDisabled ? '#B3BAC5' : '#505F79'}"
                                                icon="SolidExport"
                                            />
                                            {{ $t('bio.download') }}
                                        </button>
                                    </span>
                                </ElPopover>
                                <!-- 跳转到bio搜索结果页 -->
                                <ElPopover
                                    :visibleArrow="false"
                                    :disabled="!isOverExtract2BioLimit"
                                    popperClass="bio-panel__popover"
                                    width="200"
                                    placement="top"
                                    trigger="hover"
                                    :content="$t('bio.tip.search', ['50,000'])"
                                >
                                    <span slot="reference">
                                        <button
                                            class="pt-ui-btn"
                                            data-type="primary"
                                            :disabled="extractButtonDisabled"
                                            @click="extractSequence"
                                        >
                                            <PtIcon class="core-products-bio-icon" icon="SolidCoreProductsBio" />
                                            {{ $t('bio.toBio') }}
                                        </button>
                                    </span>
                                </ElPopover>
                            </div>
                        </div>
                    </div>
                    <SaveSequenceToWSContainer
                        v-if="isShowMSADialog"
                        :selectedSeqs="saveSeqObj"
                        @close="() => {
                            isShowMSADialog = false
                        }"
                    />
                </div>
                <div v-show="selectTab === 'family'" class="bio__structures__family">
                    <div class="bio__structures__family-title">
                        {{ $t('patent-view.lifescience.family.select.simple') }}
                        <ElTooltip
                            v-if="hasFamilyPatentSequences"
                            effect="light"
                            :content="$t('patent-view.lifescience.family.tooltip')"
                            placement="top"
                            :visibleArrow="false"
                        >
                            <PtIcon icon="SolidInfo" colour="#C2C2C2" />
                        </ElTooltip>
                    </div>
                    <div v-if="isLoading" class="bio-panel__loading">
                        <PtIcon icon="SolidLoadingBars" />
                    </div>
                    <template v-else-if="hasFamilyPatentSequences">
                        <ElTable
                            height="100%"
                            rowKey="patent_id"
                            size="small"
                            :data="familyPatentSequences"
                            :emptyText="$t('view.table.noData')"
                        >
                            <ElTableColumn type="index" width="48" label="#" />
                            <ElTableColumn
                                :label="$t('patent-view.lifescience.family.publicNum')"
                            >
                                <template #default="scope">
                                    <div
                                        class="bio__structures__family-pn"
                                        @click="openPatentViewLink(scope.row.patent_id)"
                                    >
                                        {{ scope.row.pn }}
                                    </div>
                                </template>
                            </ElTableColumn>
                            <ElTableColumn
                                prop="sequence_count"
                                :label="$t('patent-view.lifescience.family.seqCount')"
                            />
                        </ElTable>
                    </template>
                    <template v-else>
                        <PtNodata class="bio__structures__family-nodata" />
                    </template>
                </div>
            </div>
        </div>
    </draggable>
</template>

<script>
import { defineComponent, ref, computed } from 'vue';
import scrollIntoView from 'scroll-into-view-if-needed';
import _ from 'lodash';
import { toBioSearchResult, bioUrl, mergeRange, findKeyFn } from '../utils/extractSeqs';
import {
    generateSequenceList,
    generateSetting,
    checkSequenceType,
    parseSequence,
    generateQuerySequenceDropDownSet,
} from '../utils/sequence';
import BioHighlightItem from './BioHighlightItem.vue';
import {
    getFolders,
    addFolder,
    getSequenceListById,
    getSequenceCount,
    getExportQuery,
    getFamilyPatentSequences,
} from 'library-@/apis/life_science_api/life_science';
import getUrlParams from '@/utils/getQueryFromUrl';
import { loop } from '@/utils';
import BioDropdown from '../bio-popper/BioDropdown.vue';
import Draggable from '../../Draggable';
import PtNodata from '@/components/PtNodata';
import ukHandlerMixin from '@/mixins/uk-handler-mixin';
import { mapActions, mapGetters } from 'vuex';
import {
    Popover,
    Tooltip,
    Input,
    Radio,
    RadioButton,
    RadioGroup,
    Tabs,
    TabPane,
    Table,
    TableColumn,
} from 'element-ui';
import { Toggler } from 'patsnap-ui';
import { Toast, Icon } from 'patsnap-biz';
import {
    MAX_LIMIT_SEQUENCE_EXTRACT,
    MAX_LIMIT_SEQUENCE_DOWNLOAD,
    MAX_TO_MSA_COUNT,
    ROUTES_ENABLE_BIO,
    BIO_SEARCH_TYPE,
    MAX_TO_BIO_SEARCH_RESULT,
} from '../../../constants';
import BioHelperService from '../../../services/bio-helper-service';
import ClaimService from '../../../services/claim-service';
import SaveSequenceToWSContainer from '../SaveSequenceToWSContainer';
import { exportSequenceApi } from 'library-@/apis/core_basic_api/export';
import { getRouteNameList } from '../../../router';
import traverse, { traverseFn, deleteElementsStyleBySelector } from '../../../../../../components/highlight/traverse';
import infiniteScroll from 'element-ui/lib/infinite-scroll';
import $ from 'jquery';
import {
    getDefaultSettings,
    getDefaultAlgorithm,
    StateProvider,
} from '@patsnap-biz/bio-search-setting';
import '@patsnap-biz/bio-search-setting/lib/style';
import '@patsnap-ui/icon/assets/solid/LoadingBars.svg';
import '@patsnap-ui/icon/assets/solid/info.svg';
import '@patsnap-ui/icon/assets/solid/export.svg';
import '@patsnap-ui/icon/assets/solid/FolderMany.svg';
import '@patsnap-ui/icon/assets/solid/CoreProductsBio.svg';
import '@patsnap-ui/icon/assets/solid/ArrowRight.svg';
import vLoading from 'library-@/directives/loading-v2/loading.vue';
import { usePatentBasic } from '@/composables/use-patent-basic';
import BioSearchSetting from './BioSearchSetting.vue';

const AssistantPresetStateProvider = {
    extends: StateProvider,
    props: {
        manualExpand: {
            type: Boolean,
            default: false,
        },
        shortExpand: {
            type: Boolean,
            default: false,
        },
    },
};

const LIMIT_SAVE_SEQ_PER_TIME = 100000;

const HIGHLIGHT_COLOR = {
    exact: '#F79498',
    similar: '#FFD485',
    prepare: '#FFF98A',
    current: '#AB8500',
};

// 设置一个默认值，如果用户没有设置，则使用默认值
// 用户设置了值，则优先使用用户的值
const useUserSetState = (defaultValue) => {
    const predictedValue = ref(defaultValue);
    const userValue = ref(defaultValue);
    const value = computed({
        get: () => userValue.value || predictedValue.value,
        set: (val) => {
            userValue.value = val;
            predictedValue.value = val;
        },
    });
    return {
        predictedValue,
        value,
    };
};

export default defineComponent({
    name: 'BioPanel',
    directives: {
        infiniteScroll,
    },
    components: {
        vLoading,
        BioHighlightItem,
        Draggable,
        SaveSequenceToWSContainer,
        BioDropdown,
        ElPopover: Popover,
        ElTooltip: Tooltip,
        ElRadio: Radio,
        ElRadioGroup: RadioGroup,
        ElRadioButton: RadioButton,
        ElTable: Table,
        ElTableColumn: TableColumn,
        PtIcon: Icon,
        PtInput: Input,
        PtTabs: Tabs,
        PtTabPane: TabPane,
        PtToggler: Toggler,
        PtNodata,
        BioSearchSetting,
        AssistantPresetStateProvider,
    },
    mixins: [ukHandlerMixin],
    props: {
        value: {
            type: Boolean,
            default: false,
        },
        patentId: {
            type: String,
            required: true,
        },
        pn: {
            type: String,
            required: true,
        },
    },
    setup() {
        const {
            getPatentBase,
        } = usePatentBasic();

        const { predictedValue: manualExpand, value: userManualExpand } = useUserSetState(false);
        const { predictedValue: shortExpand, value: userShortExpand } = useUserSetState(false);

        return {
            getPatentBase,
            manualExpand,
            shortExpand,
            userManualExpand,
            userShortExpand,
        };
    },
    data() {
        return {
            othersEnabled: true,
            currentSeqId: '',
            bioHighlightScroller: null,
            sequenceList: {},
            isLoading: false,
            sequenceIdString: '',
            isShowMSADialog: false,
            sequencesToMSA: [],
            tabs: [],
            highlightTabName: 'selected',
            selectTab: 'highlight',
            target: undefined,
            showSearchSequence: false,
            data: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24],
            busy: false,
            inputSequence: '',
            uploadedSequences: [],
            sequenceResult: {},
            assistantId: undefined,
            page: 0,
            showSettingUpload: false,
            uploadSettigns: {},
            storeSelectedTabTarget: null,
            extractType: 'EXTRACT_ALL',
            extractList: [],
            manualCount: 0,
            totalCount: 0,
            maxIdNum: 0,
            exportLoading: false,
            familyType: 'simple',
            familyPatentSequences: undefined,
            saveSeqObj: {},
            hasBioSequences: false,
            /**
             * 初始位置，离右侧316px，400为本身宽度
             */
            initPosX: $(window)
                .width() - 316 - 400,
            lang: '',
            switchActiveItem: undefined,
        };
    },
    computed: {
        ...mapGetters({
            bioHighlightEnable: 'bio/highlightEnable',
            bioQuerySequence: 'bio/querySequence',
            bioQuerySequenceName: 'bio/querySequenceName',
            bioQuerySequences: 'bio/querySequences',
            bioSelectedSequences: 'bio/selectedSequences',
            bioSelectedSequencesProcessing: 'bio/querySequencesProcessing',
            bioSelectedTarget: 'bio/selectedTarget',
            bioSearchSettingLists: 'bio/searchSettingLists',
            bioPanelVisible: 'bio/bioPanelVisible',
            bioPanelLoading: 'bio/panelLoading',
        }),
        fromBio() {
            return getUrlParams('bio_uk');
        },
        hasFamilyPatentSequences() {
            return this.familyPatentSequences && this.familyPatentSequences.length;
        },
        save2WorkspaceTip() {
            return this.msaButtonDisabled ? this.msaManualTip : this.$t('bio.addToMSA');
        },
        isOverDownloadLimit() {
            return this.sequenceIdRange > MAX_LIMIT_SEQUENCE_DOWNLOAD;
        },
        isOverExtract2BioLimit() {
            return this.sequenceIdRange > MAX_LIMIT_SEQUENCE_EXTRACT;
        },
        msaManualTip() {
            if (this.sequenceRangeAndManual === 0) {
                return this.$t('patent-view.lifescience.extract.zero');
            }
            if (this.sequenceIdRange > this.maxIdNum) {
                return '';
            }
            return this.$t('patent-view.lifescience.extract.msa');
            // return _.isEqual(this.extractList, ['EXTRACT_MANUAL']) || this.extractType === 'EXTRACT_ALL'
            //     ? this.$t('patent-view.lifescience.extract.msa')
            //     : this.$t('bio.tip.msa', [MAX_TO_MSA_COUNT]);
        },
        extractRangeType() {
            if (this.extractType === 'EXTRACT_ALL') {
                return 'EXTRACT_ALL';
            }
            return ['EXTRACT_NUM', 'EXTRACT_MANUAL'].every(item => this.extractList.indexOf(item) !== -1) ? 'EXTRACT_RANGE' : this.extractList[0];
        },
        isRangeType() {
            return this.extractType === 'EXTRACT_RANGE';
        },
        msaButtonDisabled() {
            if (this.isRangeType) {
                if (this.extractList.includes('EXTRACT_NUM')) {
                    return !!this.errorMsg || this.sequenceIdRange <= 0 || this.sequenceIdRange > this.maxIdNum;
                }
                if (this.sequenceRangeAndManual <= 0) {
                    return true;
                }
                return false;
            }
            return false;
        },
        extractButtonDisabled() {
            if (this.isRangeType) {
                if (this.extractList.includes('EXTRACT_NUM')) {
                    return this.sequenceRangeAndManual <= 0 || this.sequenceRangeAndManual > Math.min(MAX_LIMIT_SEQUENCE_EXTRACT, this.maxIdNum);
                }
                return !this.extractList.length;
            }
            return false;
        },
        exportButtonDisabled() {
            if (this.isRangeType) {
                if (this.extractList.includes('EXTRACT_NUM')) {
                    return this.sequenceRangeAndManual <= 0 || this.sequenceRangeAndManual > Math.min(MAX_LIMIT_SEQUENCE_DOWNLOAD, this.maxIdNum);
                }
                return !this.extractList.length;
            }
            return this.totalCount > MAX_LIMIT_SEQUENCE_DOWNLOAD;
        },
        isShowRangeError() {
            return this.isRangeType && this.maxIdNum > 0 && !!this.errorMsg;
        },
        // 输入的范围字符不合法
        rangeCountNotLegal() {
            if (!this.maxIdNum) {
                return false;
            }
            return this.isSequenceIdInvalid;
        },
        isSequenceIdInvalid() {
            if (!this.formattedSequenceIdsString) return false;
            const reg = /^(?!0)\d+(-\d+)?$/;
            const arr = this.formattedSequenceIdsString.split(',');
            return arr.some(item => !reg.test(item));
        },
        isSequenceIdNumInvalid() {
            if (!this.formattedSequenceIdsString) return false;
            return this.formattedSequenceIdsArr.some(item => {
                const [start, end] = item;
                return start > end || item.some(v => v <= 0 || v > this.maxIdNum);
            });
        },
        isMaxSearchResultCount() {
            return this.totalCount > MAX_TO_BIO_SEARCH_RESULT;
        },
        /**
         * 计算range总区间长度
         * @returns {number}
         */
        sequenceIdRange() {
            const range = this.formattedSequenceIdsArr.reduce((acc, cur) => {
                const [start, end] = cur;
                if (start === 0 && end === 0) {
                    return acc;
                }
                return acc + (end - start + 1);
            }, 0);
            return range || 0;
        },
        sequenceRange() {
            if (this.errorMsg) {
                return -1;
            }
            return this.sequenceIdRange;
        },
        sequenceRangeAndManual() {
            return this.extractList.includes('EXTRACT_NUM') ? this.sequenceRange : 0 + this.extractList.includes('EXTRACT_MANUAL') ? this.manualCount : 0;
        },
        toBioExportRangeDisable() {
            return this.isRangeType ? (this.sequenceRangeAndManual <= 0 || this.sequenceRangeAndManual > MAX_LIMIT_SEQUENCE_DOWNLOAD) : this.totalCount > MAX_LIMIT_SEQUENCE_DOWNLOAD;
        },
        toBioExtractRangeDisable() {
            return this.isRangeType && (this.sequenceRangeAndManual <= 0 || this.sequenceRangeAndManual > MAX_LIMIT_SEQUENCE_EXTRACT);
        },
        /* 埋点用 */
        routeNameUpperCase() {
            return getRouteNameList(this.$route);
        },
        dropDownDataSet() {
            let r = [];
            if (this.bioQuerySequences) {
                r = r.concat(generateQuerySequenceDropDownSet(this.bioQuerySequences));
            }
            let key = 0;
            r.push({
                label: `Selected (${this.bioSelectedSequences.length}):`,
                data: this.bioSelectedSequences.map(({
                    seqId,
                    sequence,
                    seqHashId,
                }) => {
                    return {
                        key: `selected-${++key}`,
                        label: `Code ${seqId}: ${sequence}`,
                        value: sequence,
                        seqId: seqId,
                        seqHashId: seqHashId,
                    };
                }),
            });
            return r;
        },
        defaultSetting() {
            const key = this.getSelectedTargetKey();
            const targetQuery = this.bioQuerySequences.find(x => findKeyFn(x, key));

            const combined = this.currentSearchType === BIO_SEARCH_TYPE.combined ? _.get(targetQuery, 'combined_sequence')
                .find(item => item.label === this.target.labelKey) : {};
            const _setting = combined.blast_params || _.get(targetQuery, 'search_setting', _.cloneDeep(getDefaultSettings(this.currentSearchType)));
            // sequenceType
            _setting.sequenceType = _.get(combined, 'sequence_type', '') || this.currentSequenceType;
            // dbType
            _setting.dbType = _.get(combined, 'target_sequence_type', '') || _.get(this.currentSelectedSequence, 'target_sequence_type') || this.currentSequenceType;

            if (this.mismatchMode) {
                _setting.mismatches = this.getMismatches();
            }
            if (!_setting.algorithmSettings) {
                // 优先从search setting获取
                const defaultAlgorithmSettings = _.cloneDeep(getDefaultAlgorithm(_setting.sequenceType, _setting.dbType));
                if (_.get(targetQuery, 'search_setting') || _.get(targetQuery, 'combined_sequence')) {
                    const isAutomatic = _.get(_setting, 'automatic', true);
                    const fincalSetting = isAutomatic ? defaultAlgorithmSettings : _setting;

                    _setting.algorithmSettings = {
                        automatic: isAutomatic,
                        algorithm: _.get(fincalSetting, 'algorithm', ''),
                        matrix: _.get(fincalSetting, 'matrix', ''),
                        wordSize: _.get(fincalSetting, 'word_size', _.get(fincalSetting, 'wordSize', 0)),
                        gaps: _.get(fincalSetting, 'gaps', ''),
                        matchScores: _.get(fincalSetting, 'match_scores', _.get(fincalSetting, 'matchScores', '')),
                    };
                } else {
                    _setting.algorithmSettings = defaultAlgorithmSettings;
                }
            }
            return _setting;
        },
        currentSetting() {
            const key = this.getSelectedTargetKey();
            if (_.isEmpty(this.bioSearchSettingLists[key])) {
                const _setting = _.cloneDeep(this.defaultSetting);
                this['bio/setSearchSetting']({
                    key,
                    value: _setting,
                });
            }
            return this.bioSearchSettingLists[key];
        },
        // 选中的序列，对应的原始序列
        currentSelectedSequence() {
            const key = this.getSelectedTargetKey();
            const querySequence = this.bioQuerySequences.find(x => findKeyFn(x, key));
            const selectedSequence = this.bioSelectedSequences.find(x => x.seqId === key);
            return querySequence || selectedSequence;
        },
        currentSearchType() {
            return _.get(this.currentSelectedSequence, 'type', BIO_SEARCH_TYPE.sequence);
        },
        currentSequenceType() {
            return _.get(this.currentSelectedSequence, 'sequenceType')
                || _.get(this.currentSelectedSequence, 'sequence_type')
                || checkSequenceType(this.inputSequence.replace(/\s*/g, ''))
                || 'PROTEIN';
        },
        mismatchMode() {
            const labelKey = _.get(this.bioSelectedTarget, 'labelKey', '');
            return this.currentSearchType === BIO_SEARCH_TYPE.antibody &&
                (labelKey.indexOf('LC-CDR') > -1 ||
                    labelKey.indexOf('HC-CDR') > -1);
        },
        uploadSequenceValid() {
            let valid = true;
            this.uploadedSequences.forEach(seq => {
                valid = valid && seq.valid;
            });
            return valid;
        },
        formattedSequenceIdsString() {
            return this.sequenceIdString.replaceAll(/\s/g, '')
                .replaceAll(/，/g, ',');
        },
        /**
         * 提取序列的字符串格式化
         * @returns {Array<[number, number]>}
         */
        formattedSequenceIdsArr() {
            if (!this.formattedSequenceIdsString) return [];
            const arr = this.formattedSequenceIdsString.split(',');
            return mergeRange(arr.map(item => (item.includes('-') ? item.split('-')
                .map(o => _.parseInt(o)) : [_.parseInt(item), _.parseInt(item)])));
        },
        getRangeMinAndMax() {
            const nums = this.formattedSequenceIdsArr.flat();
            const min = Math.min(...nums);
            const max = Math.max(...nums);
            return {
                min,
                max,
            };
        },
        errorMsg() {
            // 非正整数，不符合正则
            if (this.isSequenceIdInvalid) {
                return this.$t('bio.errorText');
            }
            // 数字的范围错误
            if (this.isSequenceIdNumInvalid) {
                return this.$t('bio.tip.invalidRange');
            }
            return '';
        },
        /**
         * 提取时候拼的query
         * @returns {string | void}
         */
        extractStr() {
            if (!this.formattedSequenceIdsArr.length) return;
            return this.formattedSequenceIdsArr.map(item => {
                const [start, end] = item;
                return `${start}-${end}`;
            })
                .join('&');
        },
    },
    watch: {
        $route: {
            immediate: true,
            handler() {
                const path = this.$route.path.replace(/(^\/|\/$)/g, '');
                const tabs = path.split('\'');
                this.tabs = tabs;
                if (!tabs.some(tab => ROUTES_ENABLE_BIO.includes(tab))) {
                    this['bio/setBioPanelVisible'](false);
                }
                this.lastSelector = undefined;
            },
        },
        highlightTabName(newVal) {
            this['bio/setSearchMode'](newVal);
            if (newVal === 'upload') {
                setTimeout(() => {
                    this.$refs.ptInput.$refs.textarea.focus();
                }, 200);
            }
            if (!_.isEmpty(this.sequenceResult)) {
                this.showSearchSequence = !this.showSearchSequence;
            }
        },
        async patentId() {
            await this.refreshPanelData();
        },
    },
    async created() {
        this.isLoading = true;
        this.MAX_TO_MSA_COUNT = MAX_TO_MSA_COUNT;
        this.MAX_LIMIT_SEQUENCE_DOWNLOAD = MAX_LIMIT_SEQUENCE_DOWNLOAD;
        this.bioUrl = bioUrl();
        this.getFolders = getFolders;
        this.addFolder = addFolder;
        if (!this.bioSelectedSequencesProcessing) {
            this.setDefaultSelected();
        }
        await this.getSequenceCount();
        await this.initData();
        this.isLoading = false;
    },
    activated() {
        this.$nextTick(() => {
            if ($('#chemicalPanelRef')) {
                const index = $('#bioPanelRef')
                    .css('z-index');
                $('#chemicalPanelRef')
                    .css('z-index', +index - 1);
            }
        });
    },
    methods: {
        ...mapActions(['bio/toggle', 'bio/selectSequences', 'bio/setSelectTaregt',
            'bio/setSearchSetting', 'bio/setSearchSettingItem', 'bio/setLastSearchSeq',
            'bio/setLastSearchSetting', 'bio/setHitSeqNumsView', 'bio/setBioSequenceSearchResult',
            'bio/setBioPanelLoading', 'bio/setBioPanelVisible', 'bio/setSearchMode',
            'bio/setHighlightKeys']),
        async checkHasBioSequence() {
            const data = await this.getPatentBase(this.patentId);
            this.hasBioSequences = _.get(data, 'HAS_BIO_HIGHLIGHT');
        },
        async initData() {
            this.bioHelperService = new BioHelperService(this.patentId);
            this.claimService = new ClaimService(this.patentId);
            this.highlightTabName = this.fromBio ? 'selected' : 'upload';
            await this.checkHasBioSequence();
            const { lang } = await this.claimService.getPatentClaim();
            this.lang = lang;
            this.selectTab = this.hasBioSequences ? 'highlight' : 'family';
            const SELECT_TAB_FETCH = {
                'highlight': this.handleSelectSequence,
                'family': this.handleFamilyPatentSequence,
            };
            await SELECT_TAB_FETCH[this.selectTab]();
        },
        async refreshPanelData() {
            this['bio/setBioPanelLoading'](true);
            this['bio/setBioSequenceSearchResult']({});
            this.inputSequence = '';
            this.familyPatentSequences = undefined;
            this.extractType = 'EXTRACT_ALL';
            await this.syncBioPanelData(this.patentId);
            this.setDefaultSelected();
            await this.getSequenceCount();
            await this.initData();
            this['bio/setBioPanelLoading'](false);
        },
        updateSeqId({ /* highlightType, */
            keys,
            next,
            seqIdNums,
        }) {
            this.switchActiveItem = {
                keys,
                id: seqIdNums,
            };
            // FIXME: highlightType 是什么? 什么用途?
            const selector = keys.map(({ key }) => `a[selected-nos="${key}"]`)
                .join(',');
            if (this.lastSelector !== selector) {
                this.lastSelector = selector;
                this.traverse = traverse(selector, {
                    fn: traverseFn('bio-hl-current'),
                });
            }
            const { ele } = this.traverse.next(next) || {};
            if (!ele) {
                return;
            }
            this['bio/setHitSeqNumsView'](seqIdNums);
            scrollIntoView(ele, {
                scrollMode: 'if-needed',
                inline: 'end',
                block: 'nearest',
                behavior: 'smooth',
            });
        },
        /**
         * FIXME:
         * 该 method 依赖 routeName
         */
        getSequenceListById() {
            const bioUk = getUrlParams('bio_uk');
            const fields = this.tabs
                .filter(tab => ROUTES_ENABLE_BIO.includes(tab))
                .map(tab => tab.slice(0, 1))
                .join(',');
            if (bioUk && fields) {
                // this.isLoading = true;
                getSequenceListById(this.patentId, bioUk, fields)
                    .then(res => {
                        if (res.data) {
                            this.sequenceList = res.data;
                        }
                        this.isLoading = false;
                    })
                    .catch(() => {
                        this.isLoading = false;
                    });
            }
        },
        onSwitchEnable() {
            let value = !this.bioHighlightEnable;
            this['bio/toggle'](value);
        },
        onClose() {
            this.$emit('close');
        },
        extractSequence() {
            window.rvTrack.lp('Click Show All Subject Seq', {
                source: this.routeNameUpperCase,
            });
            let queryStr = this.patentId;
            queryStr += `${
                this.extractList.includes('EXTRACT_NUM') && this.extractStr
                    ? `|${this.extractStr}`
                    : '|'
            }|${this.extractRangeType}`;
            toBioSearchResult(queryStr);
        },
        async getExportQuery() {
            const data = _.assign(
                {
                    extract_type: this.extractRangeType,
                },
                this.isRangeType
                    ? {
                        ranges: this.formattedSequenceIdsArr.map(item => ({
                            start: item[0],
                            end: item[1],
                        })),
                    } : {},
            );
            const response = await getExportQuery(this.patentId, data);
            const {
                total,
                q,
                error_code,
            } = response || {};

            if (error_code === 52100001) {
                Toast({
                    message: this.$t(`bio.errorCode.${error_code}`),
                    type: 'warning',
                });
                return Promise.reject();
            }

            if (!total) {
                Toast({
                    message: this.$t('patent-view.lifescience.extract.zero'),
                    type: 'warning',
                });
                return Promise.reject();
            }
            return {
                total,
                q,
            };
        },
        async exportSequence() {
            if (this.toBioExportRangeDisable) {
                return;
            }
            this.exportLoading = true;
            window.rvTrack.lp('Confirm Download Seq Range', {
                source: this.routeNameUpperCase,
                num_records: this.sequenceIdRange,
            });

            try {
                const {
                    total,
                    q,
                } = await this.getExportQuery();
                exportSequenceApi({
                    extract_type: this.extractRangeType,
                    total,
                    q,
                    patent_id: this.bioHelperService.patentId,
                    pn: this.pn,
                    range_start: this.getRangeMinAndMax.min,
                    range_end: this.getRangeMinAndMax.max,
                })
                    .then(({ job_id }) => {
                        this.exportLoading = false;
                        if (job_id) {
                            window.open_noopener(`/export/result/${job_id}`, '_blank', 'noopener');
                        }
                    });
            } catch {
                this.exportLoading = false;
            }
        },
        /**
         * 设置默认选中的对比项
         */
        setDefaultSelected() {
            if (this.bioSelectedSequences.length || this.bioQuerySequences.length) {
                const querySeqs = _.get(this.dropDownDataSet, '[0].data', []);
                if (querySeqs.length) {
                    // antibody 默认选中接口返回带有 ‘selected_label’ 是true的序列
                    let targetSeq;
                    if ([BIO_SEARCH_TYPE.antibody, BIO_SEARCH_TYPE.combined].indexOf(_.get(this.bioQuerySequences, '[0].type')) !== -1) {
                        targetSeq = querySeqs.find(x => x.labelKey === _.get(this.bioQuerySequences, '[0].selected_label')) || querySeqs[0];
                    } else {
                        targetSeq = querySeqs[0];
                    }
                    this.target = targetSeq;
                    this['bio/setSelectTaregt'](targetSeq);
                } else {
                    // 如果找不到默认选中第一条selected
                    this.target = _.get(this.dropDownDataSet, '[1].data[0]');
                    this['bio/setSelectTaregt'](_.get(this.dropDownDataSet, '[1].data[0]'));
                }
            }
        },
        async handleSelectTarget(target) {
            if (this.target && this.target.key !== target.key) {
                this.bioHelperService.trackUserEvent({
                    action: 'ASSISTANT_SWITCH_QUERY',
                    data: {
                        patent_id: this.patentId,
                        ...(target.labelKey ? { label: target.labelKey } : {}),
                    },
                });
            }
            this.target = target;
            this['bio/setSelectTaregt'](target);
            // 切换mismatch的设置项
            this.setMismatchSetting();
            await this.handleSelectSequence();
        },
        showUploadSetting() {
            if (this.isLoading) return;
            this.showSettingUpload = true;
        },
        async preAddToMsa() {
            if (this.msaButtonDisabled) {
                return;
            }
            const {
                total,
                q,
            } = await this.getExportQuery();
            // 单次保存上限
            if (total > LIMIT_SAVE_SEQ_PER_TIME) {
                Toast({
                    message: this.$t('bio.tip.msa', [LIMIT_SAVE_SEQ_PER_TIME]),
                    type: 'warning',
                });
                return Promise.reject();
            }
            this.saveSeqObj = {
                selectedSeqs: [],
                selectedCount: total,
                extractQuery: q,
                from_module: 'PATENT',
                patentId: this.patentId,
            };
            this.isShowMSADialog = true;
        },
        async loadSequenceDetail(seqs) {
            const bioHelperService = new BioHelperService();
            const sequenceDetails = await bioHelperService.getSequenceBatch({ sequenceHashIds: seqs.map(v => v.seqId) });
            const sequenceOriginal = _.intersectionWith(this.bioSelectedSequences, seqs, (old, other) => {
                return old.seqHashId === other.seqId;
            });
            sequenceOriginal.forEach(v => {
                const seq = sequenceDetails[v.seqId];
                if (seq) {
                    v.sequence = seq.sequence;
                    v.sequence_len = seq.sequence_len;
                    v.sequence_type = seq.sequence_type;
                }
            });
            this['bio/selectSequences'](this.bioSelectedSequences);
            return sequenceDetails;
        },
        async loadMoreSequence() {
            this.busy = true;
            if (this.sequenceResult && this.page * 50 >= this.sequenceResult.similar_match_total) {
                this.busy = false;
                return;
            }
            if (this.highlightTabName === 'selected') {
                await this.getSequenceMatchResult(++this.page);
            } else {
                await this.getSequenceResult({
                    assistant_id: this.assistantId,
                    page: ++this.page,
                });
            }
            this.busy = false;
        },
        async getSequenceCount() {
            const response = await getSequenceCount(this.patentId);
            const {
                manual_count,
                max_id_num = 0,
                total_count = 0,
            } = _.get(response, 'results', {});
            this.extractList = [];
            if (max_id_num) {
                this.extractList.push('EXTRACT_NUM');
            } else if (manual_count) {
                this.extractList.push('EXTRACT_MANUAL');
            }
            this.manualCount = manual_count;
            this.maxIdNum = max_id_num;
            this.totalCount = total_count;
        },
        openPatentViewLink(patentId) {
            const { params, query } = this.$route;
            const { href } = this.$router.resolve({
                path: '/',
                query: {
                    ...query,
                    patentId,
                },
                params,
            });
            window.open_noopener(href, '_blank', 'noopener');
        },
        async onClickBioPanelTab() {
            if (this.selectTab !== 'family' || this.familyPatentSequences) {
                return;
            }
            await this.handleFamilyPatentSequence();
        },
        async handleFamilyPatentSequence() {
            this.isLoading = true;
            this.familyPatentSequences = await getFamilyPatentSequences(this.patentId);
            this.isLoading = false;
        },
        /**
         * 搜索序列
         * @returns {Promise<void>}
         */
        async handleSearchSequence() {
            this.showSearchSequence = false;
            this.isLoading = true;
            this.page = 1;

            const checkReg = /[^a-zA-Z\s]+/g;
            const uploadSeq = _.get(this.uploadedSequences, '[0].sequence', '');
            const label = _.get(this.uploadedSequences, '[0].label', '');
            if (!uploadSeq) {
                this.isLoading = false;
                return;
            }
            if (checkReg.test(uploadSeq)) {
                this.isLoading = false;
                return;
            }
            this.storeSelectedTabTarget = this.bioSelectedTarget;
            this['bio/setSelectTaregt']({
                value: uploadSeq,
                label: label,
                key: 'upload',
            });

            // 保存最后一次查询的序列，用于页面里的序列做对比，防止下拉选择的序列变更后，对比序列不对
            this['bio/setLastSearchSeq'](this.bioSelectedTarget);

            const key = this.getSelectedTargetKey();
            // 确定选中的是 query 还是 selected 的序列
            const querySequence = this.bioQuerySequences.find(x => findKeyFn(x, key));
            const selectedSequence = this.bioSelectedSequences.find(x => x.seqId === key);
            // 获取设置项
            const targetSetting = this.currentSetting;

            const manualSetting = this.bioSearchSettingLists['upload'];
            const shortSetting = this.bioSearchSettingLists['upload-short'];

            const sharedSettingFields = [
                'subject_length',
                'multi_subject_length',
                'limit',
                'ungapped',
                'evalue',
                'low_complexity_region',
            ];

            Object.assign(shortSetting, _.pick(manualSetting, sharedSettingFields));

            const assistant_id = await this.bioHelperService.submitSequence({
                sequence_list: generateSequenceList(BIO_SEARCH_TYPE.sequence, querySequence, selectedSequence,
                    this.highlightTabName === 'upload', _.get(this.uploadedSequences, '[0]'), _.get(this.bioSelectedTarget, 'labelKey'), targetSetting.mismatches),
                blast_parameter: generateSetting(BIO_SEARCH_TYPE.sequence, manualSetting),
                short_blast_parameter: generateSetting(BIO_SEARCH_TYPE.short, shortSetting),
                sequence_type: this.currentSetting.sequenceType,
                target_sequence_type: this.currentSetting.dbType,
                type: BIO_SEARCH_TYPE.sequence,
            });
            await loop(() => this.getSequenceResult({
                assistant_id,
                lang: this.lang,
                page: 1,
            }), 2000, result => result.status !== 'RUNNING');
            await this.getAllHightLightKey({
                assistant_id,
                lang: this.lang,
            });
            this['bio/setLastSearchSetting'](_.cloneDeep(this.currentSetting));
            this.assistantId = assistant_id;
            this.target = undefined;
            this.isLoading = false;
            this.showSearchSequence = true;
        },
        /**
         * 命中序列
         * @returns {Promise<void>}
         */
        async handleSelectSequence() {
            if (!this.target) return;
            this.showSearchSequence = false;
            this.isLoading = true;
            // 保存最后一次查询的序列，用于页面里的序列做对比，防止下拉选择的序列变更后，对比序列不对
            this['bio/setLastSearchSeq'](this.bioSelectedTarget);
            this.page = 1;
            try {
                await this.getSequenceMatchResult(this.page);
            } finally {
                this.isLoading = false;
                this.showSearchSequence = true;
            }
        },
        async getSequenceMatchResult(page) {
            const params = {
                ...(this.target.bioUk ? { uk: this.target.bioUk } : {}),
                ...(this.target.labelKey ? { query: this.target.labelKey } : {}),
                ...(this.target.seqId ? { sequence_id: this.target.seqId } : {}),
            };
            const result = await this.bioHelperService.getSequenceMatchResult({
                lang: this.lang,
                page: page,
                size: 50,
                ...params,
            });
            this.useLatestHighlightData({
                exact_match: result.exact_highlight_keys || [],
                similar_match: result.similar_highlight_keys || [],
            });
            this.updateSequenceResult(page, result);
        },
        async getSequenceResult({
            assistant_id,
            lang,
            page,
        }) {
            const result = await this.bioHelperService.getSequenceResult({
                assistant_id,
                page,
                lang,
            });
            this.updateSequenceResult(page, result);
            return result;
        },
        updateSequenceResult(page, result) {
            if (page === 1) {
                this.sequenceResult = result;
                // 用于保存序列到workspace的location信息
            } else if (result.similar_match) {
                const { similar_match = [] } = result;
                this.sequenceResult.similar_match.push(...similar_match);
            }
            this['bio/setBioSequenceSearchResult'](this.sequenceResult);
        },
        useLatestHighlightData(result) {
            // 删除选中高亮
            deleteElementsStyleBySelector();
            if (this.traverse) {
                this.traverse.delete('bio-hl-current');
            }

            this.highLightKey(result);
        },
        async getAllHightLightKey({
            assistant_id,
            lang,
        }) {
            const result = await this.bioHelperService.getAllHighLightKeys({
                assistant_id,
                lang,
            });
            this.useLatestHighlightData(result);
        },
        highLightKey(result) {
            // 找到第一个非跨域的styleSheet
            const styleSheet = Array.from(document.styleSheets).find(o=> !o.href);

            const highlightKeys = [
                {
                    data: _.get(result, 'exact_match', []),
                    key: 'exact',
                },
                {
                    data: _.get(result, 'similar_match', []),
                    key: 'similar',
                },
            ];

            Array.from(styleSheet.cssRules)
                .map((rule, index) => {
                    if (rule.selectorText && rule.selectorText.indexOf('a.bio-hl[selected-nos=') !== -1) {
                        return index;
                    }
                    return -1;
                })
                .filter(index => index >= 0)
                .reverse()
                .map(index => styleSheet.deleteRule(index));

            highlightKeys.forEach(item => {
                if (item.data.length) {
                    const keys = item.data.map(key => `a.bio-hl[selected-nos="${key}"]`);
                    styleSheet.insertRule(`${keys.join(',')}{ background-color: ${HIGHLIGHT_COLOR[item.key]} }`, 0);
                }
            });

            const keys = highlightKeys.map(item => item.data).flat();
            this['bio/setHighlightKeys'](keys);
        },
        getSelectedTargetKey() {
            if (this.highlightTabName === 'upload') {
                return 'upload';
            }
            const bioUk = _.get(this.bioSelectedTarget, 'bioUk');
            const labelKey = _.get(this.bioSelectedTarget, 'labelKey');
            // 生成 query 的序列对应的key, 每个query可能对应多个子序列
            const queryKey = labelKey ? `${bioUk}_${labelKey}` : bioUk;
            return queryKey || _.get(this.bioSelectedTarget, 'seqId') || '';
        },
        handleSetSearchSetting({
            key,
            value,
        }, settings) {
            const bioUk = this.getSelectedTargetKey();
            if (_.isEmpty(this.bioSearchSettingLists[bioUk])) {
                this['bio/setSearchSetting']({
                    bioUk,
                    value: settings,
                });
            }
            if (key) {
                this['bio/setSearchSettingItem']({
                    settingKey: bioUk,
                    itemKey: key,
                    value,
                });
                // 判断序列是否发生变更
                // 用默认setting和当前setting做比较
                this.compareSetting(this.defaultSetting, this.currentSetting);
            }
        },
        compareSetting(originalSetting, currentSetting) {
            // 比较第一层字段的值
            let isChangeed = false;
            for (let x of _.keys(originalSetting)) {
                // 比较第二层字段的值
                if (x === 'algorithmSettings') {
                    if (this.currentSearchType === 'MOTIF2') continue;
                    for (let y of _.keys(originalSetting[x])) {
                        isChangeed = !_.isEqual(originalSetting[x][y], currentSetting[x][y]);
                        if (isChangeed) {
                            break;
                        }
                    }
                } else {
                    isChangeed = !_.isEqual(originalSetting[x], currentSetting[x]);
                }
                if (isChangeed) {
                    break;
                }
            }
            const bioUk = this.getSelectedTargetKey();
            this['bio/setSearchSettingItem']({
                settingKey: bioUk,
                itemKey: 'isChanged',
                value: isChangeed,
            });
        },
        handleReset() {
            const bioUk = this.getSelectedTargetKey();
            this['bio/setSearchSettingItem']({
                settingKey: bioUk,
                itemKey: 'isChanged',
                value: false,
            });
        },
        setMismatchSetting() {
            if (this.mismatchMode) {
                const bioUk = this.getSelectedTargetKey();
                if (!_.get(this.currentSetting, 'mismatches')) {
                    this['bio/setSearchSettingItem']({
                        settingKey: bioUk,
                        itemKey: 'mismatches',
                        value: this.getMismatches(),
                    });
                }
            }
        },
        getMismatches() {
            const labelKey = _.get(this.bioSelectedTarget, 'labelKey', '');
            const prefix = labelKey.slice(0, 5);
            const atibodySequence = this.currentSelectedSequence.antibody_sequence;
            if (labelKey.indexOf('CDR1+2+3') > -1) {
                // 如果是选中全部的cdr，就遍历下
                return atibodySequence.filter(x => x.label !== labelKey &&
                    x.label.indexOf(prefix) > -1)
                    .filter(x => x.label.indexOf('-Variants') === -1)
                    .map(x => {
                        return {
                            label: x.label.slice(3),
                            value: x.mismatch,
                        };
                    });
            }
            const seq = atibodySequence.find(x => x.label === labelKey);
            // 如果选中某个cdr就直接返回
            if (seq) {
                return [{
                    label: labelKey.slice(3),
                    value: seq.mismatch,
                }];
            }
        },
        handleUpdateUploadSeq() {
            this.uploadedSequences = parseSequence(this.inputSequence);
            const bioUk = this.getSelectedTargetKey();
            this['bio/setSearchSettingItem']({
                settingKey: bioUk,
                itemKey: 'sequenceType',
                value: this.defaultSetting.sequenceType,
            });
            this['bio/setSearchSettingItem']({
                settingKey: bioUk,
                itemKey: 'dbType',
                value: this.defaultSetting.dbType,
            });
            const _setting = _.cloneDeep(this.defaultSetting);
            this['bio/setSearchSetting']({
                key: bioUk,
                value: _setting,
            });

            const count = {
                short: 0,
                normal: 0,
            };
            this.uploadedSequences.forEach(seq => {
                if (seq.sequence.length <= 30) {
                    count.short++;
                } else {
                    count.normal++;
                }
            });

            this.manualExpand = count.normal > 0;
            this.shortExpand = count.short > 0;
        },
        onKeyboardUpAndDown(e, direction) {
            const itemInserted = (key) => {
                try {
                    const arr = this.$refs.highlightItems || [];
                    return arr.find(vm => {
                        const nums = vm?.seqData?.sequence_id_number || [];
                        return `${nums}` === `${key}`;
                    });
                }
                catch (e) {
                    // noop
                }
            };
            if (this.switchActiveItem && itemInserted(this.switchActiveItem.id)) {
                e.preventDefault();
                this.updateSeqId({
                    keys: this.switchActiveItem.keys,
                    seqIdNums: this.switchActiveItem.id,
                    next: direction === 'next',
                });
            }
        },
    },
});
</script>

<style lang="scss">
.bio-panel__container {
    position: absolute;
    background: #fff;
    padding: 56px 0 15px 0;
    box-shadow: 0 2px 20px 0 rgba(0, 0, 0, 0.3);
    border-radius: 5px;
    width: 400px;
    color: #020A1A;
    font-size: 14px;
    box-sizing: border-box;
    text-align: left;
    cursor: move;

    .bio-panel__content {
        cursor: default;
        display: flex;
        flex-direction: column;
        height: calc(100vh - 230px);
        // .core-products-bio-icon {
        //     fill: $text-white;
        // }
    }

    .bio-panel__loading {
        position: absolute;
        width: 100%;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        text-align: center;

        .patsnap-biz-icon {
            font-size: 32px;
        }
    }

    .bio-panel__head-title {
        position: absolute;
        top: 16px;
        left: 16px;
        font-size: 16px;
        color: $text-black;
        z-index: 5;
    }

    .bio-panel__close-btn {
        position: absolute;
        top: 10px;
        right: 16px;
        font-weight: normal;
        font-size: 23px;
        color: #aaa;
        z-index: 5;
        cursor: pointer;

        &:hover {
            color: #bbb;
        }
    }

    .bio-panel__loading-container {
        height: 100%;
        display: flex;
        justify-content: center;
        align-items: center;

        .loading-icon {
            width: 80px;
            height: 80px;
        }
    }

    .bio-panel__content-container {
        height: 100%;
        display: flex;
        flex-direction: column;
    }

    .bio-panel__title {
        position: relative;
        height: 24px;
        line-height: 24px;
        margin-right: 20px;
        padding-left: 16px;

        display: flex;
        align-items: center;

        &-text {
            font-size: 14px;
            color: $text-dark-blue;
            margin-right: 2px;
        }

        .switch-button {
            display: inline-block;
            position: absolute;
            right: 5px;
            width: 50px;
            line-height: 30px;
            text-align: center;
            cursor: pointer;
            height: 20px;
            top: 50%;
            transform: translateY(-50%);
        }
    }

    .bio__structures-radio {
        margin-top: 10px;
        padding-left: 16px;
        padding-right: 16px;
        width: 100%;

        .el-radio-button {
            padding: 1px;
            border: 1px solid #B3BAC5;
            border-radius: 6px 0 0 6px;
            border-right: 0;
            width: 50%;

            &:last-child {
                border-left: 0;
                border-radius: 0 6px 6px 0;
                border-right: 1px solid #B3BAC5;

                &.only-input {
                    border: 1px solid #B3BAC5;
                    border-radius: 6px;
                }
            }
        }

        .el-radio-button__inner {
            width: 100%;
            min-width: 86px;
            border-radius: 4px;
            border: none;
            box-shadow: none;
            padding: 6px 4px;
            text-align: center;

            font-size: 14px;
            color: #5E6C84;

            &:hover {
                color: #5E6C84;
            }
        }

        .el-radio-button__orig-radio:checked + .el-radio-button__inner {
            color: #FFF;
            background-color: #505F79;
            border-color: #505F79;
        }
    }

    .bio__structures-tab {
        margin-right: 24px;
        padding-left: 16px;

        .el-tabs__active-bar {
            background-color: #45A321;
        }

        .el-tabs__item {
            padding: 0 15px;

            &.is-active {
                color: #1D8820;
            }

            &:hover {
                color: #1D8820;
            }
        }
    }

    .bio__structures__highlight {
        flex: 1;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        height: 100%;
    }

    .bio__structures__extract {
        margin: 4px 24px 0 16px;

        .el-radio .el-radio__label {
            font-weight: 400;
        }

        label {
            display: flex;
            align-items: center;

            &.disabled {
                cursor: not-allowed;
            }

            input {
                background: #fff;
                margin-right: 8px;
            }
        }
        .bio__structures__extract__row {
            display: flex;
            align-items: center;
            gap: 8px;
            margin-bottom: 4px;
        }
    }

    .bio__structures__range {
        background: #F4F5F7;
        border-radius: 4px;
        padding: 8px 8px 4px;

        &-seq {
            font-size: 12px;
            line-height: 12px;
            font-weight: 400;
            margin-bottom: 8px;
            padding-left: 22px;
        }

        .patsnap-biz-icon {
            fill: $text-disable;
        }
    }

    .bio__structures__family {
        padding: 0 24px 0 16px;
        height: 100%;
        overflow: hidden;
        display: flex;
        flex-direction: column;

        &-title {
            font-size: 14px;
            font-weight: 600;
            color: $text-dark-blue;
            margin-bottom: 16px;

            .patsnap-biz-icon {
                fill: $text-disable;
            }
        }

        &-pn {
            color: #1D8820;
            cursor: pointer;

            &:hover {
                text-decoration: underline;
            }
        }

        &-nodata {
            .pt_nodata-title {
                font-size: 14px;
                font-weight: 400;
                color: #020A1A;
                line-height: 20px;
            }
        }

        .el-table {
            color: #020A1A;
            background-color: #fff;

            &::before {
                height: 0;
            }

            &__body-wrapper {
                background-color: #fff;
            }
        }

        .el-table thead {
            color: #020A1A;
            font-weight: 400;
        }

        .el-table .el-table__cell {
            padding: 4px 0;

            .cell {
                padding-right: 0 !important;
            }
        }

        .el-table, .el-table__expanded-cell,
        .el-table thead tr,
        .el-table th.el-table__cell {
            background-color: $bg-normal;
        }

        .el-table th.el-table__cell > .cell {
            font-weight: 400;
        }

        .el-table, .el-table td, .el-table--border th, .el-table--border td {
            border-color: $bg-dark;
        }

        .el-table__fixed {
            max-height: 100% !important;
        }
    }

    .bio__structures-select {
        color: $text-dark-blue;
        margin-bottom: 10px;
        padding: 0;
        width: 100%;
        flex: 1;
        overflow-y: auto;
        padding-left: 16px;
        display: flex;
        flex-direction: column;

        .others {
            cursor: pointer;
        }
    }

    .bio__download {
        padding-top: 16px;
        margin: 0 20px 0 16px;
        border-top: 1px solid #b3bac5;

        &-title {
            font-size: 16px;
            color: $text-dark-blue;
        }

        &-rows {
            display: flex;
            margin-top: 4px;
            height: 30px;

            &-left {
                margin-left: 22px;
                margin-bottom: 8px;
                display: flex;
                align-items: center;

                &-inputs {
                    flex: 1;
                    padding: 0 5px;
                    height: 28px;
                    line-height: 28px;
                    border: 1px solid $neutral-blue-60;
                    border-radius: 4px;
                    vertical-align: middle;
                    display: table-cell;
                    background: #fff;
                    margin-left: 8px;

                    input {
                        width: 100%;
                        height: 24px;
                        border: 0;
                        padding: 0;
                        font-size: 14px;
                        color: $text-dark-blue;
                    }

                    span {
                        color: $text-hint;
                    }

                    &-error {
                        border: 1px solid $text-error;
                    }
                }
            }

            &-download {
                margin-top: 12px;
                padding-top: 16px;
                border-top: 1px solid #DFE1E6;

                .button-container {
                    display: flex;
                    align-items: center;

                    .icon-button {
                        cursor: pointer;
                        background-color: #f3f5f9;
                        border-radius: 3px;
                        text-align: center;
                        border: 1px solid $neutral-blue-60;
                        width: 36px;
                        height: 30px;
                        line-height: 25px;

                        &:hover {
                            background: $neutral-blue-30;
                        }
                    }

                    .icon-disable {
                        color: #f4f5f7;
                        cursor: not-allowed;
                        border-color: #f4f5f7;

                        &:hover {
                            background: #f4f5f7;
                        }
                    }

                    .pt-ui-btn {
                        padding: 2px 10px;
                        margin-left: 10px;
                    }

                    .pt-ui-btn[data-type=default] {
                        border: 1px solid #B3BAC5;
                    }
                }
            }
        }

        &-errors {
            margin-top: 4px;
            color: #ed314a;
            font-size: 12px;
            line-height: 14px;
            padding-left: 75px;
        }
    }

    .bio-panel {
        &__structures-content {
            position: relative;
            margin-top: 10px;
            flex: 1;
            overflow-x: hidden;
        }

        &__list-title {
            line-height: 28px;
            display: flex;
            align-items: center;
            margin: 4px 20px 0 0;
        }

        &__no-sequence {
            padding-left: 15px;
        }

        &__dot {
            display: inline-block;
            width: 8px;
            height: 8px;
            border-radius: 50%;
            vertical-align: middle;
        }

        &__dot-exact {
            background: #f79498;
        }

        &__dot-selected {
            background: #ffd485;
        }

        &__dot-others {
            background: #94d0f7;
        }

        &__selected {
            font-size: 14px;
            color: $text-hint;
            vertical-align: middle;
            display: inline-flex;
            flex: 1;
            margin-left: 4px;
            margin-right: 15px;
            align-items: center;

            .patsnap-biz-icon {
                fill: $text-disable;
            }
        }

        &__jump-title {
            //float: right;
            font-size: 14px;
            color: $text-hint;
            //margin-right: 10px;
        }
    }
}

.bio-panel__title-tip {
    word-break: break-word;
    white-space: pre;
    padding: 8px!important;
    border: 1px solid #DFE1E6!important;
}

.bio-panel__popover {
    color: $text-dark-blue;
    font-size: 12px;
    padding: 8px;
    text-align: left;
    word-break: normal;
    word-break: break-word;
}

.bio-panel__setting-panel {
    padding: 0 !important;
}

.bio-sequence {
    margin-top: 13px;

    &__label {
        font-size: 12px;
        font-weight: 400;
        color: #061632;
        margin-bottom: 4px;
        text-align: left;
    }

    &__select {
        display: block;

        .bio-drop-down,
        .for-active-search {
            width: 100% !important;
        }
    }

    &__search-tip {
        color: #8993A4;
        font-size: 14px;
        font-weight: 400;
        line-height: 24px;
        text-align: left;
    }
}

.bio-textarea.el-textarea {
    width: 100%;
    margin: 10px 0 4px 0;
    flex-shrink: 0;

    .el-input__inner:focus,
    .el-textarea__inner:focus {
        border-color: #45a321;
    }

    textarea {
        resize: none;
    }
}

.bio-findseq {
    height: 30px;
    padding: 0 15px;
    display: block;
    font-size: 14px;
    font-weight: 400;
    margin-top: 12px;
    margin-right: 20px;
    min-height: 30px;
    line-height: 24px;
}

.bio-sequence__row {
    padding-right: 20px;

    .upload-input-wrap {
        display: flex;
        flex-direction: column;
    }

    .bio-sequence__row--setting {
        color: #1976D2;
        padding: 0 8px;
        display: inline-flex;
        align-items: center;
        height: 28px;
        line-height: 28px;

        &.changed {
            background: #DDEBF8;
            padding: 4px;
            border-radius: 4px;
        }

        &.disabled {
            color: #A3C8ED;
            cursor: not-allowed;
        }

        &:hover {
            text-decoration: underline;
        }

        &.disabled {
            color: #B3BAC5;
            text-decoration: none;
            cursor: not-allowed;
        }

        .bio-upload--setting {
            &__arrow-right {
                color: inherit;
            }
        }
    }
}

.bio-error-info {
    color: #D11A32;
}
</style>
